import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import HumanoidIntelligenceShell, { MetricCard, UnknownValue } from "../../components/humanoid/HumanoidIntelligenceShell";
import { getNumericPrice, getPurchasePriceLabel, getSpecificationLabel, humanoidCatalogStats, humanoidManufacturers, humanoidRobots } from "../../data/humanoidCatalog";

const priceBands = [
  ["all", "All prices"],
  ["under-10000", "Under $10K"],
  ["10000-25000", "$10K–$25K"],
  ["25000-50000", "$25K–$50K"],
  ["50000-100000", "$50K–$100K"],
  ["above-100000", "Above $100K"],
  ["undisclosed", "No recorded USD purchase price"],
];

function matchesPrice(robot, band) {
  if (band === "all") return true;
  const price = getNumericPrice(robot);
  if (band === "undisclosed") return price === null;
  if (price === null) return false;
  if (band === "under-10000") return price < 10000;
  if (band === "10000-25000") return price >= 10000 && price < 25000;
  if (band === "25000-50000") return price >= 25000 && price < 50000;
  if (band === "50000-100000") return price >= 50000 && price < 100000;
  if (band === "above-100000") return price >= 100000;
  return true;
}

function priceLabel(robot) {
  return getPurchasePriceLabel(robot);
}

export default function HumanoidDatabasePage() {
  const [query, setQuery] = useState("");
  const [country, setCountry] = useState("all");
  const [status, setStatus] = useState("all");
  const [manufacturer, setManufacturer] = useState("all");
  const [price, setPrice] = useState("all");
  const [sort, setSort] = useState("name");

  const countries = [...new Set(humanoidRobots.map((robot) => robot.robotOriginCountry))].sort();
  const statuses = [...new Set(humanoidRobots.map((robot) => robot.status))].sort();

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const rows = humanoidRobots.filter((robot) => {
      const haystack = [robot.name, robot.manufacturerName, robot.robotOriginCountry, robot.category, robot.useCases, ...(robot.targetMarkets || [])].join(" ").toLowerCase();
      return (!needle || haystack.includes(needle))
        && (country === "all" || robot.robotOriginCountry === country)
        && (status === "all" || robot.status === status)
        && (manufacturer === "all" || robot.manufacturerId === manufacturer)
        && matchesPrice(robot, price);
    });
    return rows.sort((a, b) => {
      if (sort === "price-low") return (getNumericPrice(a) ?? Infinity) - (getNumericPrice(b) ?? Infinity) || a.name.localeCompare(b.name);
      if (sort === "price-high") return (getNumericPrice(b) ?? -1) - (getNumericPrice(a) ?? -1) || a.name.localeCompare(b.name);
      if (sort === "height") return (b.heightCm || 0) - (a.heightCm || 0) || a.name.localeCompare(b.name);
      if (sort === "dof") return (b.degreesOfFreedom || 0) - (a.degreesOfFreedom || 0) || a.name.localeCompare(b.name);
      return a.name.localeCompare(b.name);
    });
  }, [query, country, status, manufacturer, price, sort]);

  return (
    <HumanoidIntelligenceShell
      title="Humanoid Robot Database"
      description="Compare humanoid robots by manufacturer, country, commercial status, public price, height, weight and degrees of freedom using the source-linked catalog. Configuration notes are retained with each measurement."
      path="/humanoid-robots/database"
      eyebrow="SEARCH • FILTER • VERIFY"
      recordCount={humanoidRobots.length}
      modifiedTime={humanoidCatalogStats.lastVerifiedAt}
      reviewLabel={`Most recent source review ${humanoidCatalogStats.lastVerifiedAt}; dates vary by record`}
    >
      <section className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard value={humanoidCatalogStats.robotCount} label="Robots" detail="Current retained catalog" />
        <MetricCard value={humanoidCatalogStats.manufacturerCount} label="Manufacturers" detail="Distinct manufacturers in this catalog" />
        <MetricCard value={humanoidCatalogStats.countryCount} label="Countries" detail="Robot origin country" />
        <MetricCard value={humanoidCatalogStats.publicPriceCount} label="Public prices" detail="Robots with a recorded price plan" />
      </section>

      <section className="mt-8 border border-white/10 bg-[#07101f]/90 p-4 sm:p-5">
        <label className="block text-xs font-black uppercase tracking-[0.16em] text-cyan-300" htmlFor="humanoid-search">Search robot, company, country or application</label>
        <input id="humanoid-search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Unitree G1, Figure, China, research…" className="mt-3 w-full border border-white/15 bg-[#030712] px-4 py-3 text-base font-bold text-white outline-none placeholder:text-slate-600 focus:border-cyan-300/60" />
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <select aria-label="Filter manufacturer" value={manufacturer} onChange={(event) => setManufacturer(event.target.value)} className="border border-white/15 bg-[#030712] px-3 py-3 text-sm font-bold text-white">
            <option value="all">All manufacturers</option>
            {humanoidManufacturers.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
          <select aria-label="Filter country" value={country} onChange={(event) => setCountry(event.target.value)} className="border border-white/15 bg-[#030712] px-3 py-3 text-sm font-bold text-white">
            <option value="all">All countries</option>
            {countries.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
          <select aria-label="Filter status" value={status} onChange={(event) => setStatus(event.target.value)} className="border border-white/15 bg-[#030712] px-3 py-3 text-sm font-bold text-white">
            <option value="all">All statuses</option>
            {statuses.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
          <select aria-label="Filter price" value={price} onChange={(event) => setPrice(event.target.value)} className="border border-white/15 bg-[#030712] px-3 py-3 text-sm font-bold text-white">
            {priceBands.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <select aria-label="Sort robots" value={sort} onChange={(event) => setSort(event.target.value)} className="border border-white/15 bg-[#030712] px-3 py-3 text-sm font-bold text-white">
            <option value="name">Sort name</option>
            <option value="price-low">USD purchase price low to high</option>
            <option value="price-high">USD purchase price high to low</option>
            <option value="height">Height highest</option>
            <option value="dof">DoF highest</option>
          </select>
        </div>
        <p className="mt-4 text-sm font-bold text-slate-400">{filtered.length} matching robots</p>
      </section>

      <p className="mt-4 text-sm leading-7 text-slate-400">Price bands and price sorting use recorded USD purchase offers. Deposits and recurring plans are excluded. Dimensions and joint counts may refer to different configurations; open each profile for its measurement notes.</p>

      <section className="mt-6 hidden overflow-x-auto border border-white/10 lg:block">
        <table className="w-full min-w-[1100px] border-collapse text-left text-sm">
          <thead className="bg-white/[0.06] text-[11px] uppercase tracking-[0.14em] text-slate-400">
            <tr>
              {['Robot','Manufacturer','Country','Status','Height','Weight','DoF','Purchase price','Source date'].map((header) => <th key={header} scope="col" className="px-4 py-3">{header}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10 bg-[#07101f]/85">
            {filtered.map((robot) => (
              <tr key={robot.id} className="align-top hover:bg-white/[0.035]">
                <th scope="row" className="px-4 py-4"><Link to={`/humanoid-robots/${robot.slug}`} className="font-black text-white hover:text-cyan-300">{robot.name}</Link><p className="mt-1 text-xs font-medium text-slate-500">{robot.category}</p></th>
                <td className="px-4 py-4 font-bold text-slate-300">{robot.manufacturerName}</td>
                <td className="px-4 py-4 text-slate-300">{robot.robotOriginCountry}</td>
                <td className="px-4 py-4 text-slate-300">{robot.status}</td>
                <td className="px-4 py-4 tabular-nums text-slate-300"><UnknownValue value={getSpecificationLabel(robot, "heightCm")} /></td>
                <td className="px-4 py-4 tabular-nums text-slate-300"><UnknownValue value={getSpecificationLabel(robot, "weightKg")} /></td>
                <td className="px-4 py-4 tabular-nums text-slate-300"><UnknownValue value={getSpecificationLabel(robot, "degreesOfFreedom")} /></td>
                <td className="px-4 py-4 font-bold text-cyan-200">{priceLabel(robot)}</td>
                <td className="px-4 py-4 text-xs text-slate-400">{robot.lastVerifiedAt}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="mt-6 grid gap-3 lg:hidden">
        {filtered.map((robot) => (
          <article key={robot.id} className="border border-white/10 bg-[#07101f]/90 p-5">
            <div className="flex items-start justify-between gap-3">
              <div><Link to={`/humanoid-robots/${robot.slug}`} className="text-xl font-black text-white">{robot.name}</Link><p className="mt-1 text-sm font-bold text-slate-400">{robot.manufacturerName} • {robot.robotOriginCountry}</p></div>
              <span className="border border-white/10 bg-white/[0.04] px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-slate-300">{robot.status}</span>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-px bg-white/10 text-sm">
              <div className="bg-[#030712] p-3"><p className="text-xs text-slate-500">Height</p><p className="mt-1 font-black text-white"><UnknownValue value={getSpecificationLabel(robot, "heightCm")} /></p></div>
              <div className="bg-[#030712] p-3"><p className="text-xs text-slate-500">Weight</p><p className="mt-1 font-black text-white"><UnknownValue value={getSpecificationLabel(robot, "weightKg")} /></p></div>
              <div className="bg-[#030712] p-3"><p className="text-xs text-slate-500">DoF</p><p className="mt-1 font-black text-white"><UnknownValue value={getSpecificationLabel(robot, "degreesOfFreedom")} /></p></div>
              <div className="bg-[#030712] p-3"><p className="text-xs text-slate-500">Price</p><p className="mt-1 font-black text-cyan-200">{priceLabel(robot)}</p></div>
            </div>
          </article>
        ))}
      </section>

      {!filtered.length && <div className="mt-6 border border-white/10 bg-white/[0.035] p-8 text-center text-slate-400">No retained robot matches these filters.</div>}
    </HumanoidIntelligenceShell>
  );
}
